/**
 * Data Mappers Service
 * Transform CESVI API response to UI component formats
 */

/**
 * Map CESVI version to VehicleIdentificationCard format
 * @param {Object} cesviVersion - CESVI version object
 * @param {string} vin - Original VIN
 * @returns {Object} - Mapped vehicle data
 */
export const mapCesviToVehicle = (cesviVersion, vin) => {
  if (!cesviVersion) return null;

  return {
    vin: vin,
    make: cesviVersion.marca || 'No disponible',
    model: cesviVersion.modelo || 'No disponible',
    year: cesviVersion.ano?.toString() || 'No disponible',
    vehicleType: cesviVersion.segmento || cesviVersion.tipo || 'No disponible',
    bodyType: cesviVersion.tipo || 'No disponible',
    vehicleClass: cesviVersion.clase || 'No disponible',
    countryOfOrigin: 'México',
    manufacturingPlant: 'No disponible',
    serialNumber: vin.slice(-6),
    engineCode: cesviVersion.motor || 'No disponible',
    transmission: cesviVersion.transmision || 'No disponible',
    fuelType: cesviVersion.combustible || 'No disponible',
    doors: cesviVersion.puertas || 'No disponible',
    passengers: cesviVersion.pasajeros || 'No disponible',
    drivetrain: cesviVersion.traccion || 'No disponible'
  };
};

/**
 * Map CESVI version to VersionSelector format
 * @param {Object} cesviVersion - CESVI version object
 * @returns {Object} - Mapped version data
 */
export const mapCesviToVersion = (cesviVersion) => {
  if (!cesviVersion) return null;

  return {
    id: cesviVersion.claveUnica,
    trim: cesviVersion.versionComercial || 'Versión base',
    description: `${cesviVersion.tipo || ''} - ${cesviVersion.clase || ''}`.trim() || 'Sin descripción',
    engine: formatEngine(cesviVersion),
    transmission: cesviVersion.transmision || 'No especificada',
    drivetrain: cesviVersion.traccion || 'No especificada',
    listPrice: formatCurrency(cesviVersion.precioLista),
    // Keep original data for equipment mapping
    _original: cesviVersion
  };
};

/**
 * Map CESVI version to EquipmentAccordion format
 * @param {Object} cesviVersion - CESVI version object
 * @returns {Object} - Mapped equipment categories
 */
export const mapCesviToEquipment = (cesviVersion) => {
  if (!cesviVersion) return getEmptyEquipment();

  return {
    safety: [
      createEquipmentItem('Bolsas de Aire', cesviVersion.bolsasAire, 'BA001'),
      createEquipmentItem('Frenos Delanteros', cesviVersion.frenosDelanteros, 'FD001')
    ].filter(item => item.available),

    engine: [
      createEquipmentItem('Motor', `${cesviVersion.motor || 'N/A'} - ${cesviVersion.numeroCilindros || 'N/A'}`, 'MOT001', true),
      createEquipmentItem('Potencia', cesviVersion.potencia, 'POT001'),
      createEquipmentItem('Válvulas', cesviVersion.valvulas ? `${cesviVersion.valvulas} válvulas` : null, 'VAL001'),
      createEquipmentItem('Combustible', cesviVersion.combustible, 'COMB001')
    ].filter(item => item.available),

    drivetrain: [
      createEquipmentItem('Transmisión', cesviVersion.transmision, 'TRANS001'),
      createEquipmentItem('Tracción', cesviVersion.traccion, 'TRAC001'),
      createEquipmentItem('Dirección', cesviVersion.tipoDireccion, 'DIR001')
    ].filter(item => item.available),

    suspension: [
      createEquipmentItem('Suspensión Delantera', cesviVersion.suspensionDelantera, 'SD001'),
      createEquipmentItem('Suspensión Trasera', cesviVersion.suspensionTrasera, 'ST001')
    ].filter(item => item.available),

    wheels: [
      createEquipmentItem('Tipo de Rin', cesviVersion.tipoRin, 'RIN001'),
      createEquipmentItem('Medida de Rin', cesviVersion.rin, 'RIN002'),
      createEquipmentItem('Neumático', cesviVersion.neumatico, 'NEU001')
    ].filter(item => item.available),

    comfort: [
      createEquipmentItem('Aire Acondicionado', cesviVersion.aireAcondicionado, 'AC001'),
      createEquipmentItem('Elevadores Eléctricos', cesviVersion.elevadoresElectricos, 'EE001'),
      createEquipmentItem('Tapicería', cesviVersion.interiores, 'INT001'),
      createEquipmentItem('Quemacocos', cesviVersion.quemacocos, 'QC001'),
      createEquipmentItem('Techo', cesviVersion.techo, 'TECHO001')
    ].filter(item => item.available),

    infotainment: [
      createEquipmentItem('Sistema de Audio', cesviVersion.estereo, 'EST001')
    ].filter(item => item.available)
  };
};

/**
 * Map CESVI version to CommercialValuesCard format
 * @param {Object} cesviVersion - CESVI version object
 * @returns {Object} - Mapped commercial data
 */
export const mapCesviToCommercial = (cesviVersion) => {
  if (!cesviVersion) return getEmptyCommercial();

  return {
    listPrice: cesviVersion.precioLista || 0,
    salePrice: cesviVersion.precioVenta || cesviVersion.precioLista || 0,
    purchasePrice: cesviVersion.precioCompra || null,
    currency: 'MXN',
    lastUpdated: new Date().toISOString().split('T')[0],
    source: 'CESVI México'
  };
};

/**
 * Create a query record for localStorage
 * @param {Object} params - Query parameters
 * @returns {Object} - Query record
 */
export const createQueryRecord = ({
  vin,
  vehicle,
  status,
  latency,
  versionsCount,
  commercialValue,
  user,
  deviceInfo
}) => {
  return {
    user: user || { email: 'unknown', name: 'Unknown', role: 'unknown' },
    vin,
    vehicle: vehicle ? {
      brand: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      version: vehicle.vehicleType,
      segment: vehicle.vehicleType
    } : null,
    status,
    latency: Math.round(latency),
    versionsCount: versionsCount || 0,
    commercialValue: commercialValue || 0,
    location: deviceInfo?.location || { city: 'CDMX', state: 'CDMX', ip: '192.168.1.1' },
    device: deviceInfo?.device || 'Desktop',
    browser: deviceInfo?.browser || 'Chrome',
    dataSource: 'CESVI México',
    apiVersion: 'v1.0'
  };
};

// Helper functions

const createEquipmentItem = (name, description, code, alwaysAvailable = false) => ({
  name,
  description: description || 'No disponible',
  available: alwaysAvailable || !!description,
  code
});

const formatEngine = (cesviVersion) => {
  const parts = [];
  if (cesviVersion.motor) parts.push(cesviVersion.motor);
  if (cesviVersion.numeroCilindros) parts.push(cesviVersion.numeroCilindros);
  if (cesviVersion.potencia) parts.push(cesviVersion.potencia);
  return parts.length > 0 ? parts.join(' - ') : 'No especificado';
};

const formatCurrency = (value) => {
  if (!value) return 'No disponible';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const getEmptyEquipment = () => ({
  safety: [],
  engine: [],
  drivetrain: [],
  suspension: [],
  wheels: [],
  comfort: [],
  infotainment: []
});

const getEmptyCommercial = () => ({
  listPrice: 0,
  salePrice: 0,
  purchasePrice: null,
  currency: 'MXN',
  lastUpdated: null,
  source: null
});

export default {
  mapCesviToVehicle,
  mapCesviToVersion,
  mapCesviToEquipment,
  mapCesviToCommercial,
  createQueryRecord
};
